import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { useTheme } from "../../contexts/ThemeContext";
import { createTicketRefresher, embedOriginOf, handleEmbedMessage, openDashboard, postToEmbed, themedEmbedUrl } from "../../lib/optimuszappEmbed";
import {
  MessageCircle,
  ExternalLink,
  ShieldCheck,
  Zap,
  CheckCircle2,
  RefreshCw,
  Building2,
  Lock,
  ArrowRight,
  Maximize2,
  Minimize2,
  Users,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Clock,
  Radio,
  Receipt,
  LayoutTemplate,
  Activity,
  Settings2,
  Sparkles,
} from "lucide-react";
import { PRODUCT_NAME } from "../../brand";

const PRESET_HEX_MAP = {
  electric: "#FF3B30",
  ocean: "#3B82F6",
  forest: "#22C55E",
  onex: "#009444",
};

// Shown in place of an embedded screen whose launch ticket was refused, until the user retries.
function SurfaceRetry({ label, onRetry }) {
  return (
    <div className="flex h-full w-full items-center justify-center">
      <div className="flex flex-col items-center gap-3 text-center max-w-sm">
        <AlertTriangle className="w-6 h-6 text-amber-500" />
        <p className="text-xs text-muted-foreground">{label} could not be opened: WhatsApp did not issue a new session.</p>
        <button
          type="button"
          onClick={onRetry}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Retry
        </button>
      </div>
    </div>
  );
}

export default function WhatsAppConnectPage() {
  const { theme, colorPreset } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get("tab");
  const initialActiveTab =
    tabParam === "templates"
      ? "templates"
      : tabParam === "billing" || tabParam === "account-billing"
      ? "billing"
      : tabParam === "activities" || tabParam === "activity"
      ? "activities"
      : tabParam === "sync"
      ? "sync"
      : "console";

  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [statusData, setStatusData] = useState(null);
  const [activeTab, setActiveTab] = useState(initialActiveTab); // "console" | "sync" | "templates" | "billing" | "activities"
  const [hasOpenedTemplates, setHasOpenedTemplates] = useState(initialActiveTab === "templates");
  const [templatesLaunchUrl, setTemplatesLaunchUrl] = useState("");
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [hasOpenedBilling, setHasOpenedBilling] = useState(initialActiveTab === "billing");
  const [billingLaunchUrl, setBillingLaunchUrl] = useState("");
  const [billingLoading, setBillingLoading] = useState(false);
  const [hasOpenedActivities, setHasOpenedActivities] = useState(initialActiveTab === "activities");
  const [activitiesLaunchUrl, setActivitiesLaunchUrl] = useState("");
  const [activitiesLoading, setActivitiesLoading] = useState(false);
  const [launchUrl, setLaunchUrl] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);

  // Sync state
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncLoading, setSyncLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [showSkipped, setShowSkipped] = useState(false);

  const containerRef = useRef(null);
  const iframeRef = useRef(null);
  const templatesIframeRef = useRef(null);
  const billingIframeRef = useRef(null);
  const activitiesIframeRef = useRef(null);
  // Surfaces whose refresh ticket was refused (bounded retries gave up): they show Retry instead of their iframe.
  const [stoppedSurfaces, setStoppedSurfaces] = useState({});
  const ticketRefresher = useRef(null);
  if (!ticketRefresher.current) {
    ticketRefresher.current = createTicketRefresher({
      onGiveUp: (surface, err) => {
        console.warn(`[WHATSAPP_CONNECT] ${surface} refresh ticket refused; waiting for Retry:`, err?.response?.status || err?.message);
        setStoppedSurfaces((prev) => ({ ...prev, [surface]: true }));
      },
    });
  }

  const activePrimary = PRESET_HEX_MAP[colorPreset] || "#FF3B30";

  const fetchSyncStatus = async () => {
    try {
      setSyncLoading(true);
      const res = await api.get("/v1/whatsapp/contacts/status");
      if (res.data && res.data.success) {
        setSyncStatus(res.data);
      }
    } catch (err) {
      console.error("Failed to load contact sync status:", err);
    } finally {
      setSyncLoading(false);
    }
  };

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await api.get("/v1/whatsapp/status");
      if (res.data && res.data.success) {
        setStatusData(res.data);
        if (res.data.launch_url) {
          setLaunchUrl(res.data.launch_url);
        }
        if (res.data.connected) {
          fetchSyncStatus();
        }
      }
    } catch (err) {
      console.error("Failed to load WhatsApp status:", err);
      toast.error("Could not fetch WhatsApp connection status");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  // Listen for fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const embedOrigin = statusData?.embed_origin || "";

  const resolveTheme = useCallback(
    () =>
      theme === "system"
        ? typeof window !== "undefined" && window.matchMedia("(prefers-color-scheme: dark)").matches
          ? "dark"
          : "light"
        : theme,
    [theme]
  );

  // Inbox iframe URL (from /status) with explicit theme tokens; only an /embed/ screen on the embed_origin it came with
  const themedLaunchUrl = useMemo(
    () => themedEmbedUrl(launchUrl, embedOrigin, { theme: resolveTheme(), preset: colorPreset, primary: activePrimary }),
    [launchUrl, embedOrigin, resolveTheme, colorPreset, activePrimary]
  );

  // The page's embedded screens: each iframe, the surface it shows (one launch ticket per iframe) and its origin —
  // the embed_origin its launch URL came with (each tab's URL is kept only when it is on that origin).
  const embedFrames = useCallback(
    () => [
      { surface: "inbox", window: iframeRef.current?.contentWindow, origin: embedOriginOf(themedLaunchUrl) },
      { surface: "templates", window: templatesIframeRef.current?.contentWindow, origin: embedOriginOf(templatesLaunchUrl) },
      { surface: "billing", window: billingIframeRef.current?.contentWindow, origin: embedOriginOf(billingLaunchUrl) },
      { surface: "activities", window: activitiesIframeRef.current?.contentWindow, origin: embedOriginOf(activitiesLaunchUrl) },
    ],
    [themedLaunchUrl, templatesLaunchUrl, billingLaunchUrl, activitiesLaunchUrl]
  );

  // A fresh single-use launch URL for one surface (an iframe's first load, or a new window).
  const fetchSurfaceUrl = useCallback(
    async (surface) => {
      const res = await api.post("/v1/whatsapp/launch-ticket", { surface });
      return themedEmbedUrl(res.data?.launch_url, res.data?.embed_origin, {
        theme: resolveTheme(),
        preset: colorPreset,
        primary: activePrimary,
      });
    },
    [resolveTheme, colorPreset, activePrimary]
  );

  // Broadcast real-time theme & preset updates to embedded OptimusZapp iframes (each only to its own origin)
  const sendThemeToIframe = useCallback(() => {
    const postToFrame = ({ window: targetWindow, origin }) => {
      if (!targetWindow || !origin) return;
      try {
        const resolvedTheme = resolveTheme();

        const themePayload = {
          type: "OPTIMUS_THEME_CHANGE",
          theme: resolvedTheme,
          mode: resolvedTheme,
          colorPreset,
          preset: colorPreset,
          primary: activePrimary,
          accent: activePrimary,
          fontFamily: "Manrope",
          headingFontFamily: "Barlow Condensed",
          monoFontFamily: "JetBrains Mono",
        };

        postToEmbed(targetWindow, origin, themePayload);
        postToEmbed(targetWindow, origin, { type: "OPTIMUSZAPP_THEME_UPDATE", payload: themePayload });
      } catch (err) {
        console.warn("Theme postMessage broadcast failed:", err);
      }
    };

    embedFrames().forEach(postToFrame);
  }, [resolveTheme, colorPreset, activePrimary, embedFrames]);

  // Real-time postMessage dispatch to embedded OptimusZapp iframe on theme/color change
  useEffect(() => {
    sendThemeToIframe();
  }, [sendThemeToIframe]);

  // Listen for messages from our iframes only (ready, silent token refresh): each iframe that asks gets its own
  // fresh ticket for its own screen, posted only to it.
  useEffect(() => {
    const handleIframeMessage = (event) =>
      handleEmbedMessage(event, {
        frames: embedFrames(),
        requestTicket: (frame) =>
          ticketRefresher.current.request(frame.surface, async () => {
            const res = await api.post("/v1/whatsapp/launch-ticket", { surface: frame.surface });
            return res.data;
          }),
        onReady: () => sendThemeToIframe(),
      });

    window.addEventListener("message", handleIframeMessage);
    return () => window.removeEventListener("message", handleIframeMessage);
  }, [sendThemeToIframe, embedFrames]);

  const handleConnect = async () => {
    try {
      setConnecting(true);
      toast.info("Connecting to Optimus WhatsApp & provisioning workspace...");
      const res = await api.post("/v1/whatsapp/connect");

      if (res.data && res.data.success) {
        toast.success(res.data.message || "WhatsApp Workspace connected successfully!");
        await fetchStatus();
      } else {
        toast.error(res.data.detail || "Connection failed");
      }
    } catch (err) {
      console.error("Connect failed:", err);
      const detail = err.response?.data?.detail;
      const errorMsg =
        (typeof detail === "object" ? detail?.message : detail) ||
        err.response?.data?.message ||
        err.message ||
        "Failed to connect WhatsApp";
      toast.error(errorMsg);
    } finally {
      setConnecting(false);
    }
  };

  const handleTriggerSync = async () => {
    try {
      setIsSyncing(true);
      toast.info("Synchronizing Gym members to OptimusZapp...");
      const res = await api.post("/v1/whatsapp/contacts/sync");
      if (res.data && res.data.success) {
        const tel = res.data.telemetry || {};
        toast.success(
          `Sync complete: ${tel.created || 0} created, ${tel.updated || 0} updated, ${tel.skipped_invalid_phone || 0} skipped.`
        );
        await fetchSyncStatus();
      } else {
        toast.error(res.data.detail || "Sync failed");
      }
    } catch (err) {
      console.error("Contact sync failed:", err);
      const errorMsg =
        err.response?.data?.detail || err.response?.data?.message || err.message || "Failed to sync contacts";
      toast.error(errorMsg);
    } finally {
      setIsSyncing(false);
    }
  };

  const [openingWorkspace, setOpeningWorkspace] = useState(false);
  const handleOpenWorkspace = async () => {
    try {
      setOpeningWorkspace(true);
      const res = await api.post("/v1/whatsapp/workspace-launch");
      if (!openDashboard(res.data?.launch_url)) toast.error("Could not open the WhatsApp workspace");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error((typeof detail === "object" ? detail?.message : detail) || "Could not open the WhatsApp workspace");
    } finally {
      setOpeningWorkspace(false);
    }
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  const isConnected = statusData?.connected;
  const orgName = statusData?.organization?.name || "Gym Organization";
  const workspace = statusData?.workspace;
  const lastSync = syncStatus?.last_sync;

  // Loads one tab's iframe once, with its own fresh single-use launch ticket.
  const loadSurfaceOnce = useCallback(
    async (surface, label, currentUrl, isLoading, setUrl, setIsLoading) => {
      if (currentUrl || isLoading) return;
      try {
        setIsLoading(true);
        const url = await fetchSurfaceUrl(surface);
        if (url) {
          setUrl(url);
        } else {
          toast.error(`Failed to obtain launch ticket for ${label}`);
          setStoppedSurfaces((prev) => ({ ...prev, [surface]: true }));
        }
      } catch (err) {
        console.error(`Failed to load ${label} ticket:`, err?.response?.status || err?.message);
        toast.error(`Could not connect to ${label}`);
        setStoppedSurfaces((prev) => ({ ...prev, [surface]: true }));
      } finally {
        setIsLoading(false);
      }
    },
    [fetchSurfaceUrl]
  );

  // "Retry" on a stopped surface: answer its refresh requests again and load it with a fresh launch ticket.
  const retrySurface = async (surface) => {
    ticketRefresher.current.retry(surface);
    setStoppedSurfaces((prev) => {
      const next = { ...prev };
      delete next[surface];
      return next;
    });
    if (surface === "inbox") {
      await fetchStatus();
      return;
    }
    const tabs = {
      templates: ["WhatsApp Templates", setTemplatesLaunchUrl, setTemplatesLoading],
      billing: ["Accounts & Billing", setBillingLaunchUrl, setBillingLoading],
      activities: ["WhatsApp Activities", setActivitiesLaunchUrl, setActivitiesLoading],
    };
    const [label, setUrl, setIsLoading] = tabs[surface];
    setUrl("");
    await loadSurfaceOnce(surface, label, "", false, setUrl, setIsLoading);
  };

  const handleOpenTemplatesTab = useCallback(async () => {
    setActiveTab("templates");
    setHasOpenedTemplates(true);
    await loadSurfaceOnce("templates", "WhatsApp Templates", templatesLaunchUrl, templatesLoading, setTemplatesLaunchUrl, setTemplatesLoading);
  }, [loadSurfaceOnce, templatesLaunchUrl, templatesLoading]);

  const handleOpenBillingTab = useCallback(async () => {
    setActiveTab("billing");
    setHasOpenedBilling(true);
    await loadSurfaceOnce("billing", "Accounts & Billing", billingLaunchUrl, billingLoading, setBillingLaunchUrl, setBillingLoading);
  }, [loadSurfaceOnce, billingLaunchUrl, billingLoading]);

  const handleOpenActivitiesTab = useCallback(async () => {
    setActiveTab("activities");
    setHasOpenedActivities(true);
    await loadSurfaceOnce("activities", "WhatsApp Activities", activitiesLaunchUrl, activitiesLoading, setActivitiesLaunchUrl, setActivitiesLoading);
  }, [loadSurfaceOnce, activitiesLaunchUrl, activitiesLoading]);

  // Tab switcher with URL query parameter sync (?tab=inbox|sync|templates|billing|activities)
  const switchTab = useCallback((nextTab) => {
    setActiveTab(nextTab);
    const updatedParams = new URLSearchParams(searchParams);
    const queryTabValue = nextTab === "console" ? "inbox" : nextTab;
    updatedParams.set("tab", queryTabValue);
    setSearchParams(updatedParams, { replace: true });

    if (nextTab === "templates") {
      handleOpenTemplatesTab();
    } else if (nextTab === "billing") {
      handleOpenBillingTab();
    } else if (nextTab === "activities") {
      handleOpenActivitiesTab();
    }
  }, [searchParams, setSearchParams, handleOpenTemplatesTab, handleOpenBillingTab, handleOpenActivitiesTab]);

  // Sync state if URL query param changes externally (e.g. browser back/forward)
  useEffect(() => {
    const raw = searchParams.get("tab");
    if (raw === "templates") {
      setActiveTab("templates");
      setHasOpenedTemplates(true);
      handleOpenTemplatesTab();
    } else if (raw === "billing" || raw === "account-billing") {
      setActiveTab("billing");
      setHasOpenedBilling(true);
      handleOpenBillingTab();
    } else if (raw === "activities" || raw === "activity") {
      setActiveTab("activities");
      setHasOpenedActivities(true);
      handleOpenActivitiesTab();
    } else if (raw === "sync") {
      setActiveTab("sync");
    } else if (raw === "inbox" || raw === "console") {
      setActiveTab("console");
    }
  }, [searchParams, handleOpenTemplatesTab, handleOpenBillingTab, handleOpenActivitiesTab]);

  // If not connected yet, render the onboarding & connection state
  if (!isConnected && !loading) {
    return (
      <div className="max-w-5xl mx-auto py-12 px-4 space-y-8">
        <div className="text-center space-y-3">
          <div className="inline-flex p-3 rounded-2xl bg-primary/10 text-primary border border-primary/20 shadow-lg">
            <MessageCircle className="w-10 h-10" />
          </div>
          <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight uppercase text-foreground">
            WhatsApp Business Hub
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl mx-auto">
            Connect your Gym organization to OptimusZapp with automated multi-tenant isolation, real-time member sync, and dedicated inbox channels.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card/80 backdrop-blur p-8 shadow-xl max-w-2xl mx-auto text-center space-y-6">
          <div className="p-4 rounded-xl bg-muted/40 border border-border flex items-center justify-between text-left">
            <div className="space-y-0.5">
              <span className="text-xs uppercase tracking-wider font-mono text-muted-foreground">Gym Organization</span>
              <p className="text-base font-bold text-foreground flex items-center gap-2">
                <Building2 className="w-4 h-4 text-primary" />
                <span>{orgName}</span>
              </p>
            </div>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-500 border border-amber-500/20">
              Disconnected
            </span>
          </div>

          <div className="space-y-3 text-left text-xs text-muted-foreground">
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Isolated Layer 3 tenant workspace under {PRODUCT_NAME} provider</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>Automatic real-time sync for active gym members with E.164 phone identity</span>
            </div>
            <div className="flex items-center gap-2.5">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>1:1 theme matching with Gym CRM Electric Blaze, Ocean, Forest, and Onex presets</span>
            </div>
          </div>

          <button
            onClick={handleConnect}
            disabled={connecting}
            className="w-full py-3.5 px-6 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-heading font-bold uppercase tracking-wider text-sm shadow-[0_0_15px_rgba(255,59,48,0.3)] transition-all hover:shadow-[0_0_25px_rgba(255,59,48,0.5)] flex items-center justify-center gap-2 disabled:opacity-50"
          >
            {connecting ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Auto-Provisioning Workspace...</span>
              </>
            ) : (
              <>
                <Zap className="w-4 h-4" />
                <span>Connect WhatsApp Workspace</span>
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  // Connected state: Full-Cover Immersive Workspace Viewport
  return (
    <div
      ref={containerRef}
      className={`flex flex-col bg-background border border-border rounded-xl overflow-hidden shadow-2xl transition-all ${
        isFullscreen
          ? "fixed inset-0 z-50 rounded-none border-none h-screen w-screen"
          : "h-[calc(100vh-5.5rem)] w-full"
      }`}
      data-testid="whatsapp-full-page-container"
    >
      {/* Tactical Top Bar */}
      <div className="shrink-0 flex items-center justify-between px-4 py-2.5 bg-card/90 backdrop-blur border-b border-border text-xs z-10">
        {/* Left: Workspace Identity & Badges */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <div className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-heading font-bold uppercase tracking-wider text-foreground text-sm flex items-center gap-1.5">
              <MessageCircle className="w-4 h-4 text-primary" />
              <span>WhatsApp Hub</span>
            </span>
          </div>

          <span className="hidden sm:inline-block text-border font-mono">|</span>

          <div className="hidden sm:flex items-center gap-2">
            <span className="text-muted-foreground font-medium">{workspace?.name || orgName}</span>
            <span className="px-2 py-0.5 rounded-md bg-emerald-500/10 text-emerald-500 border border-emerald-500/20 font-mono text-[10px] uppercase tracking-wide flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              <span>Layer 3 Isolated</span>
            </span>
          </div>
        </div>

        {/* Center: Mode Tabs */}
        <div className="flex items-center bg-muted/60 p-0.5 rounded-lg border border-border">
          <button
            onClick={() => switchTab("console")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium text-xs transition ${
              activeTab === "console"
                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
            }`}
          >
            <Radio className="w-3.5 h-3.5" />
            <span>Inbox</span>
          </button>

          <button
            onClick={() => switchTab("sync")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium text-xs transition ${
              activeTab === "sync"
                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
            }`}
          >
            <Users className="w-3.5 h-3.5" />
            <span>Member Sync</span>
            {lastSync?.skipped_invalid_phone > 0 && (
              <span className="px-1.5 py-0.2 rounded-full bg-amber-500 text-black text-[10px] font-bold">
                !
              </span>
            )}
          </button>

          <button
            onClick={() => switchTab("templates")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium text-xs transition ${
              activeTab === "templates"
                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
            }`}
          >
            <LayoutTemplate className="w-3.5 h-3.5" />
            <span>Templates</span>
          </button>

          <button
            onClick={() => switchTab("billing")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium text-xs transition ${
              activeTab === "billing"
                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
            }`}
          >
            <Receipt className="w-3.5 h-3.5" />
            <span>Accounts & Billing</span>
          </button>

          <button
            onClick={() => switchTab("activities")}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-md font-medium text-xs transition ${
              activeTab === "activities"
                ? "bg-primary text-primary-foreground font-semibold shadow-sm"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/80"
            }`}
          >
            <Activity className="w-3.5 h-3.5" />
            <span>Activities</span>
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleFullscreen}
            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
            title={isFullscreen ? "Exit Fullscreen" : "Cover Entire Screen"}
          >
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>

          {launchUrl && (
            <button
              onClick={async () => {
                // The iframe already used its ticket: a new window gets its own.
                try {
                  const url = await fetchSurfaceUrl("inbox");
                  if (url) window.open(url, "_blank", "noopener");
                } catch (err) {
                  toast.error("Could not open the WhatsApp console");
                }
              }}
              className="hidden md:flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition"
              title="Open WhatsApp Console in New Window"
            >
              <span>Launch Tab</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}

          {isConnected && statusData?.whatsapp_access && (
            <button
              onClick={handleOpenWorkspace}
              disabled={openingWorkspace}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition disabled:opacity-60"
              title="Open the full WhatsApp workspace in a new tab"
            >
              <span>Open WhatsApp workspace</span>
              <ExternalLink className="w-3.5 h-3.5" />
            </button>
          )}

          <button
            onClick={() => {
              fetchStatus();
              fetchSyncStatus();
            }}
            disabled={loading || syncLoading}
            className="p-1.5 rounded-lg border border-border hover:bg-muted text-muted-foreground hover:text-foreground transition"
            title="Refresh Status"
          >
            <RefreshCw className={`w-4 h-4 ${loading || syncLoading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {/* Main Viewport Content */}
      <div className="flex-1 w-full relative overflow-hidden bg-background">
        {/* Tab 1: WhatsApp Console (Full Height Edge-to-Edge, kept in DOM to preserve session) */}
        <div className={`w-full h-full ${activeTab === "console" ? "block" : "hidden"}`}>
          {stoppedSurfaces.inbox ? (
            <SurfaceRetry label="The WhatsApp console" onRetry={() => retrySurface("inbox")} />
          ) : themedLaunchUrl ? (
            <iframe
              ref={iframeRef}
              src={themedLaunchUrl}
              title="Optimus WhatsApp CRM Console"
              className="w-full h-full border-none bg-background"
              allow="clipboard-write; camera; microphone"
            />
          ) : statusData && (statusData.whatsapp_access === false || statusData.launch_error) ? (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center max-w-sm">
                <AlertTriangle className="w-6 h-6 text-amber-500" />
                <p className="text-xs text-muted-foreground">
                  {statusData.whatsapp_access === false
                    ? "You do not have WhatsApp permission. Ask the gym owner to grant View or Manage WhatsApp."
                    : `The WhatsApp console could not be opened (${statusData.launch_error}). Use Refresh to try again.`}
                </p>
              </div>
            </div>
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <div className="flex flex-col items-center gap-3 text-center">
                <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                <p className="text-xs text-muted-foreground">Authenticating session & loading workspace...</p>
              </div>
            </div>
          )}
        </div>

        {/* Tab 2: Member Sync & Diagnostics Hub */}
        <div className={`h-full overflow-y-auto p-6 md:p-8 space-y-6 max-w-5xl mx-auto ${activeTab === "sync" ? "block" : "hidden"}`}>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-5 border-b border-border">
              <div className="space-y-1">
                <h2 className="text-xl font-bold font-heading uppercase tracking-wide text-foreground flex items-center gap-2">
                  <Users className="w-5 h-5 text-primary" />
                  <span>Member Directory & WhatsApp Sync</span>
                </h2>
                <p className="text-xs text-muted-foreground">
                  Synchronize your active Gym members with E.164 phone identity and custom attributes to OptimusZapp.
                </p>
              </div>

              <button
                onClick={handleTriggerSync}
                disabled={isSyncing || syncLoading}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground text-xs font-heading font-bold uppercase tracking-wider shadow-md transition disabled:opacity-50"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${isSyncing ? "animate-spin" : ""}`} />
                <span>{isSyncing ? "Syncing..." : "Sync Members Now"}</span>
              </button>
            </div>

            {/* Metrics Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl border border-border bg-card/60 space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Total Gym Members
                </span>
                <p className="text-2xl font-bold font-mono text-foreground">
                  {syncLoading && !syncStatus ? "..." : syncStatus?.total_gym_members ?? 0}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-card/60 space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Synced in WhatsApp
                </span>
                <p className="text-2xl font-bold font-mono text-emerald-500">
                  {lastSync ? (lastSync.total_synced ?? (lastSync.created || 0) + (lastSync.updated || 0)) : 0}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-card/60 space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Last Created / Updated
                </span>
                <p className="text-sm font-semibold text-foreground mt-1">
                  {lastSync ? (
                    <span>
                      <span className="text-emerald-500 font-bold">+{lastSync.created || 0}</span> new /{" "}
                      <span className="text-blue-400 font-bold">{lastSync.updated || 0}</span> updated
                    </span>
                  ) : (
                    <span className="text-muted-foreground">No sync history</span>
                  )}
                </p>
              </div>

              <div className="p-4 rounded-xl border border-border bg-card/60 space-y-1">
                <span className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">
                  Last Sync Time
                </span>
                <p className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 mt-2">
                  <Clock className="w-3.5 h-3.5 text-primary" />
                  <span>
                    {lastSync?.last_sync_at ? new Date(lastSync.last_sync_at).toLocaleString() : "Never synced"}
                  </span>
                </p>
              </div>
            </div>

            {/* Real-time sync guarantee */}
            <div className="flex items-center gap-2.5 p-3.5 rounded-xl bg-card border border-border text-xs text-muted-foreground">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />
              <span>
                <strong className="text-foreground font-semibold">Real-Time Sync Active:</strong> Any newly created member, profile update, or member deletion in Gym CRM automatically synchronizes to OptimusZapp in the background.
              </span>
            </div>

            {/* Warning banner for skipped non-E.164 phone numbers */}
            {lastSync?.skipped_invalid_phone > 0 && (
              <div className="rounded-xl border border-amber-500/20 bg-amber-500/10 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-xs font-semibold text-amber-500">
                    <AlertTriangle className="w-4 h-4" />
                    <span>
                      {lastSync.skipped_invalid_phone} member(s) skipped due to non-standard phone numbers
                    </span>
                  </div>
                  {lastSync.skipped_samples && lastSync.skipped_samples.length > 0 && (
                    <button
                      onClick={() => setShowSkipped(!showSkipped)}
                      className="flex items-center gap-1 text-[11px] font-medium text-amber-400 hover:underline"
                    >
                      <span>{showSkipped ? "Hide Details" : "View Members"}</span>
                      {showSkipped ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                    </button>
                  )}
                </div>
                <p className="text-[11px] text-muted-foreground leading-relaxed">
                  OptimusZapp enforces strict international E.164 format (+&lt;country_code&gt;&lt;subscriber_digits&gt;). Ensure member phone numbers in Gym CRM are valid 10-digit Indian numbers or include valid country codes.
                </p>

                {showSkipped && lastSync.skipped_samples && (
                  <div className="overflow-x-auto rounded-lg border border-border bg-background/80 p-2 mt-2">
                    <table className="w-full text-[11px] text-left">
                      <thead>
                        <tr className="border-b border-border text-muted-foreground font-mono uppercase">
                          <th className="py-1 px-2 font-medium">Name</th>
                          <th className="py-1 px-2 font-medium">Entered Phone</th>
                          <th className="py-1 px-2 font-medium">Issue</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {lastSync.skipped_samples.map((item, idx) => (
                          <tr key={idx} className="hover:bg-muted/30">
                            <td className="py-1.5 px-2 font-medium text-foreground">{item.name || "Unknown"}</td>
                            <td className="py-1.5 px-2 font-mono text-muted-foreground">{item.phone || "(empty)"}</td>
                            <td className="py-1.5 px-2 text-amber-400">{item.reason}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>

        {/* Tab 3: WhatsApp Templates (Lazy loaded on first switch with fresh ticket, kept in DOM) */}
        {hasOpenedTemplates && (
          <div className={`w-full h-full ${activeTab === "templates" ? "block" : "hidden"}`}>
            {stoppedSurfaces.templates ? (
              <SurfaceRetry label="WhatsApp Templates" onRetry={() => retrySurface("templates")} />
            ) : templatesLaunchUrl ? (
              <iframe
                ref={templatesIframeRef}
                src={templatesLaunchUrl}
                title="Optimus WhatsApp Templates Manager"
                className="w-full h-full border-none bg-background"
                allow="clipboard-write; camera; microphone"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Authenticating session & loading Templates...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 4: Accounts & Billing (Lazy loaded on first switch with fresh ticket, kept in DOM) */}
        {hasOpenedBilling && (
          <div className={`w-full h-full ${activeTab === "billing" ? "block" : "hidden"}`}>
            {stoppedSurfaces.billing ? (
              <SurfaceRetry label="Accounts & Billing" onRetry={() => retrySurface("billing")} />
            ) : billingLaunchUrl ? (
              <iframe
                ref={billingIframeRef}
                src={billingLaunchUrl}
                title="Optimus WhatsApp Accounts & Billing"
                className="w-full h-full border-none bg-background"
                allow="clipboard-write; camera; microphone"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Authenticating session & loading Accounts & Billing...</p>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tab 5: Activities & Observability (Lazy loaded on first switch with fresh ticket, kept in DOM) */}
        {hasOpenedActivities && (
          <div className={`w-full h-full ${activeTab === "activities" ? "block" : "hidden"}`}>
            {stoppedSurfaces.activities ? (
              <SurfaceRetry label="WhatsApp Activities" onRetry={() => retrySurface("activities")} />
            ) : activitiesLaunchUrl ? (
              <iframe
                ref={activitiesIframeRef}
                src={activitiesLaunchUrl}
                title="Optimus WhatsApp Activities & Observability"
                className="w-full h-full border-none bg-background"
                allow="clipboard-write; camera; microphone"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center">
                <div className="flex flex-col items-center gap-3 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  <p className="text-xs text-muted-foreground">Authenticating session & loading Activities...</p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
